import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, Typography, Button } from 'antd';
import { FromToProps } from '@remirror/core-types';
import { useAttrs, useCommands, useEditorState, useHelpers } from '@remirror/react';
import { getMarkRange } from '@remirror/core-utils';
import { useTranslation } from '../../../../../../../../i18n/useTranslation';
import {
    sanitizeUrl,
    sanitizeToPlainText,
    createUrlValidationRule,
    createTextValidationRule,
    createFormValidationHandler,
    createUrlBlurHandler,
    createTextBlurHandler,
} from '../../../../../../../shared/utils/sanitizationUtils';

type LinkModalProps = {
    visible: boolean;
    handleClose: () => void;
};

export const LinkModal = (props: LinkModalProps) => {
    const { visible, handleClose } = props;
    const { t } = useTranslation();

    const [trPos, setTrPos] = useState<FromToProps>({ from: 0, to: 0 });
    const [isFormValid, setIsFormValid] = useState(false);
    const [form] = Form.useForm();

    const commands = useCommands();
    const helpers = useHelpers();
    const editorState = useEditorState();

    const href = (useAttrs().link()?.href as string) ?? '';

    useEffect(() => {
        if (visible) {
            const { from, to } = editorState.selection;
            const pos = getMarkRange(editorState.doc.resolve(from), 'link') || { from, to };

            form.setFieldsValue({
                href,
                text: helpers.getTextBetween(pos.from, pos.to, editorState.doc) || '',
            });

            setTrPos(pos);

            // Set initial validation state
            setIsFormValid(!!href);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const sanitizedUrl = sanitizeUrl(values.href);
            const sanitizedText = sanitizeToPlainText(values.text);
            const displayText = sanitizedText || sanitizedUrl;

            // Don't add link if URL is unsafe after sanitization
            if (!sanitizedUrl) {
                console.warn('Link URL blocked for security reasons:', values.href);
                return;
            }

            commands.replaceText({
                content: displayText,
                selection: trPos,
                type: 'link',
                attrs: {
                    href: sanitizedUrl,
                },
            });

            form.resetFields();
            handleClose();
        } catch (e) {
            console.log('Validate Failed:', e);
        }
    };

    return (
        <Modal
            title={t('Add Link')}
            onCancel={handleClose}
            visible={visible}
            centered
            footer={[
                <Button key="cancel" onClick={handleClose}>
                    {t('Cancel')}
                </Button>,
                <Button key="save" htmlType="submit" type="primary" onClick={handleOk} disabled={!isFormValid}>
                    {t('Save')}
                </Button>,
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                colon={false}
                requiredMark={false}
                onKeyPress={(e) => e.key === 'Enter' && form.submit()}
                onFieldsChange={createFormValidationHandler(form, setIsFormValid, ['href'])}
            >
                <Form.Item
                    name="href"
                    label={<Typography.Text strong>{t('Link URL')}</Typography.Text>}
                    rules={[{ required: true, message: t('Link URL is required') }, createUrlValidationRule(t)]}
                >
                    <Input placeholder="https://www.google.com" autoFocus onBlur={createUrlBlurHandler(form, 'href')} />
                </Form.Item>
                <Form.Item
                    name="text"
                    label={<Typography.Text strong>{t('Text')}</Typography.Text>}
                    rules={[createTextValidationRule(t, 'Text', 200)]}
                >
                    <Input onBlur={createTextBlurHandler(form, 'text')} />
                </Form.Item>
            </Form>
        </Modal>
    );
};
